import React, { useState, useEffect } from 'react';
import { MoreHorizontal, Check, X, Trash2, Shield, ShieldCheck, Loader2, Mail } from 'lucide-react';
import { useCreateAdmin, useGetAdmins, useGetTrashedAdmins, useToggleBlockAdmin, useDeleteAdmin, useRestoreAdmin, useHardDeleteAdmin, useBulkDeleteAdmins } from '../../services/auth/auth';
import { useAlert } from '../../context/AlertContext';
import { toast } from 'react-toastify';

const AdminsTable = () => {
    const [view, setView] = useState('active'); // 'active' | 'trash'
    const [email, setEmail] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [admins, setAdmins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const { mutateAsync: createAdmin, isPending: isInviting } = useCreateAdmin();
    const { mutateAsync: fetchAdmins } = useGetAdmins();
    const { mutateAsync: fetchTrashed } = useGetTrashedAdmins();
    const { mutateAsync: toggleBlock } = useToggleBlockAdmin();
    const { mutateAsync: deleteAdmin } = useDeleteAdmin();
    const { mutateAsync: restoreAdmin } = useRestoreAdmin();
    const { mutateAsync: hardDeleteAdmin } = useHardDeleteAdmin();
    const { mutateAsync: bulkDelete } = useBulkDeleteAdmins();
    const setAlert = useAlert();

    const loadAdmins = async () => {
        setIsLoading(true);
        try {
            const response = await (view === 'active' ? fetchAdmins() : fetchTrashed());
            setAdmins(response.admins.map(u => ({ ...u, selected: false })));
        } catch (error) {
            toast.error('Failed to load admins');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAdmins();
    }, [view]);

    const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
    const [showRowDropdown, setShowRowDropdown] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [blockReason, setBlockReason] = useState("");
    const [isActioning, setIsActioning] = useState(false);

    const toggleAdminSelection = (adminId) => {
        setAdmins(admins.map(admin =>
            admin._id === adminId ? { ...admin, selected: !admin.selected } : admin
        ));
    };

    const selectAllAdmins = () => {
        const allSelected = admins.every(admin => admin.selected);
        setAdmins(admins.map(admin => ({ ...admin, selected: !allSelected })));
    };

    const handleDelete = async (adminId) => {
        try {
            setIsActioning(true);
            if (view === 'active') {
                await deleteAdmin(adminId);
                toast.success("Admin moved to trash");
            } else {
                await hardDeleteAdmin(adminId);
                toast.success("Admin permanently deleted");
            }
            setAdmins(admins.filter(a => a._id !== adminId));
            closeModal();
        } catch (error) {
            toast.error("Failed to delete admin");
        } finally {
            setIsActioning(false);
        }
    };

    const handleRestore = async (adminId) => {
        try {
            setIsActioning(true);
            await restoreAdmin(adminId);
            toast.success("Admin restored");
            setAdmins(admins.filter(a => a._id !== adminId));
            closeModal();
        } catch (error) {
            toast.error("Failed to restore admin");
        } finally {
            setIsActioning(false);
        }
    };

    const handleBulkDelete = async () => {
        const selectedIds = admins.filter(u => u.selected).map(u => u._id);
        if (selectedIds.length === 0) return;

        try {
            setIsActioning(true);
            await bulkDelete({ ids: selectedIds, action: view === 'active' ? 'soft' : 'hard' });
            toast.success(`${selectedIds.length} admins ${view === 'active' ? 'moved to trash' : 'permanently deleted'}`);
            setAdmins(admins.filter(u => !u.selected));
            setShowHeaderDropdown(false);
        } catch (error) {
            toast.error("Failed to process bulk deletion");
        } finally {
            setIsActioning(false);
        }
    };

    const handleToggleBlock = async () => {
        if (!selectedAdmin) return;
        try {
            setIsActioning(true);
            await toggleBlock({ id: selectedAdmin._id, reason: blockReason });
            toast.success(`Admin ${selectedAdmin.isBlocked ? 'unblocked' : 'blocked'} successfully`);

            // Update local state
            setAdmins(admins.map(a => a._id === selectedAdmin._id ? { ...a, isBlocked: !a.isBlocked } : a));
            closeModal();
        } catch (error) {
            toast.error("Failed to update status");
        } finally {
            setIsActioning(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        try {
            await createAdmin({ email });
            toast.success('Admin invited successfully!');
            setShowInviteModal(false);
            setEmail('');
            loadAdmins(); 
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to invite admin');
        }
    };

    const openModal = (action, admin) => {
        setSelectedAdmin({ ...admin, action });
        setBlockReason("");
        setShowModal(true);
        setShowRowDropdown(null);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedAdmin(null);
        setBlockReason("");
    };

    const CustomCheckbox = ({ checked, onChange, indeterminate = false }) => (
        <div
            className={`w-5 h-5 border-2 rounded cursor-pointer flex items-center justify-center transition-colors ${checked || indeterminate ? 'bg-[#4B718F] border-[#4B718F]' : 'border-gray-300 dark:border-[#32353c] bg-white dark:bg-[#1d2026]'
                }`}
            onClick={onChange}
        >
            {(checked || indeterminate) && (
                <Check className="w-3 h-3 text-white" />
            )}
        </div>
    );

    const StatusBadge = ({ activated, blocked }) => {
        if (blocked) {
            return <span className="px-3 py-1 rounded-full text-xs font-medium border-[1px] border-red-500 dark:border-red-500/50 text-red-500 dark:text-red-400">Blocked</span>;
        }
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium border-[1px] ${activated
                ? 'border-[#1FA860] text-[#1FA860] dark:text-green-400 dark:border-green-500/50'
                : 'border-yellow-500 text-yellow-500 dark:text-yellow-400 dark:border-yellow-500/50'
                }`}>
                {activated ? 'Activated' : 'Pending'}
            </span>
        );
    };

    const selectedCount = admins.filter(a => a.selected).length;
    const isIndeterminate = selectedCount > 0 && selectedCount < admins.length;
    const allSelected = selectedCount === admins.length && admins.length > 0;

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-6">
                   <button onClick={() => setView('active')} className={`text-lg font-semibold ${view === 'active' ? 'text-[#4B718F] dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>Active Admins</button>
                   <button onClick={() => setView('trash')} className={`text-lg font-semibold ${view === 'trash' ? 'text-[#4B718F] dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>Trash</button>
                </div>
                <button
                    onClick={() => setShowInviteModal(true)}
                    className="bg-[#174568] text-white px-4 py-2 rounded shadow hover:bg-[#0f3a4f] transition-colors flex items-center gap-2"
                >
                    <span>+ New Admin</span>
                </button>
            </div>

            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 min-h-[300px]">
                {/* Header Actions */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-[#32353c] flex justify-end">
                    <div className="relative">
                        <button
                            onClick={() => setShowHeaderDropdown(!showHeaderDropdown)}
                            className="p-1 bg-[#F4F7FE] dark:bg-[#1d2026] rounded-full"
                        >
                            <MoreHorizontal className="w-5 h-5 text-[#4318FF]" />
                        </button>
                        {showHeaderDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-20">
                                <button
                                    onClick={selectAllAdmins}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm dark:text-gray-300"
                                >
                                    <Check className="w-4 h-4" />
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                </button>
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={selectedCount === 0 || isActioning}
                                    className="w-full px-4 py-2 text-left text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {view === 'active' ? 'Move Selected to Trash' : 'Delete Selected Permanently'} ({selectedCount})
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-[#1d2026]/60">
                            <tr>
                                <th className="px-6 py-3 text-left">
                                    <CustomCheckbox
                                        checked={allSelected}
                                        indeterminate={isIndeterminate}
                                        onChange={selectAllAdmins}
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Role</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider text-nowrap">Date Added</th>
                                <th className="px-6 py-3" />
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-[#141414] divide-y divide-gray-200 dark:divide-gray-800 text-[14px]">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-20 text-center">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#174568]" />
                                    </td>
                                </tr>
                            ) : admins.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-20 text-center text-gray-500 dark:text-gray-400">
                                        No admins found in {view}.
                                    </td>
                                </tr>
                            ) : (
                                admins.map((admin) => (
                                    <tr key={admin._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <CustomCheckbox
                                                checked={admin.selected}
                                                onChange={() => toggleAdminSelection(admin._id)}
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                            {admin.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <div className="flex items-center gap-2">
                                                {admin.role === 'super-admin' ? <ShieldCheck className="w-4 h-4 text-purple-600" /> : <Shield className="w-4 h-4 text-blue-600" />}
                                                <span className="capitalize">{admin.role}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge activated={admin.isActivated} blocked={admin.isBlocked} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-400 dark:text-gray-500">
                                            {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="relative">
                                                <button
                                                    onClick={() => setShowRowDropdown(showRowDropdown === admin._id ? null : admin._id)}
                                                    className="p-1 bg-[#F4F7FE] dark:bg-[#1d2026] rounded-full"
                                                >
                                                    <MoreHorizontal className="w-5 h-5 text-[#4318FF]" />
                                                </button>
                                                {showRowDropdown === admin._id && (
                                                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-20">
                                                        {view === 'active' ? (
                                                            <>
                                                                <button
                                                                    onClick={() => openModal('block', admin)}
                                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm dark:text-gray-300"
                                                                >
                                                                    <Shield className="w-4 h-4" />
                                                                    {admin.isBlocked ? 'Unblock' : 'Block'}
                                                                </button>
                                                                <button
                                                                    onClick={() => openModal('delete', admin)}
                                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 text-red-600 flex items-center gap-2 text-sm"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                    Move to Trash
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    onClick={() => openModal('restore', admin)}
                                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-green-600 dark:text-green-400 text-sm"
                                                                >
                                                                    <Check className="w-4 h-4" />
                                                                    Restore
                                                                </button>
                                                                <button
                                                                    onClick={() => openModal('delete', admin)}
                                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 text-red-600 flex items-center gap-2 text-sm"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                    Delete Permanently
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal for Block/Delete/Restore */}
            {showModal && selectedAdmin && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white dark:bg-[#0b0e14] rounded-xl shadow-2xl max-w-sm w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {selectedAdmin.action === 'block' && `${selectedAdmin.isBlocked ? 'Unblock' : 'Block'} Admin`}
                                {selectedAdmin.action === 'delete' && (view === 'active' ? 'Move to Trash' : 'Delete Permanently')}
                                {selectedAdmin.action === 'restore' && 'Restore Admin'}
                            </h3>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X size={20}/></button>
                        </div>
                        
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {selectedAdmin.action === 'block' && `Are you sure you want to ${selectedAdmin.isBlocked ? 'unblock' : 'block'} ${selectedAdmin.email}?`}
                                {selectedAdmin.action === 'delete' && (view === 'active' ? `Move ${selectedAdmin.email} to trash?` : `Permanently delete ${selectedAdmin.email}? This cannot be undone.`)}
                                {selectedAdmin.action === 'restore' && `Restore ${selectedAdmin.email} access?`}
                            </p>

                            {selectedAdmin.action === 'block' && !selectedAdmin.isBlocked && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-500 mb-1 uppercase tracking-wider">Reason (Optional)</label>
                                    <textarea
                                        value={blockReason}
                                        onChange={(e) => setBlockReason(e.target.value)}
                                        className="w-full p-2 border border-gray-200 dark:border-[#32353c] rounded-lg text-sm bg-gray-50 dark:bg-inherit focus:bg-white dark:focus:bg-gray-800 focus:ring-1 focus:ring-[#174568] transition-all outline-none dark:text-white"
                                        rows="3"
                                        placeholder="Enter reason for blocking..."
                                    />
                                </div>
                            )}

                            <div className="flex gap-2 pt-2">
                                <button onClick={closeModal} className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-gray-300">Cancel</button>
                                <button 
                                    onClick={selectedAdmin.action === 'restore' ? () => handleRestore(selectedAdmin._id) : 
                                             selectedAdmin.action === 'block' ? handleToggleBlock : 
                                             () => handleDelete(selectedAdmin._id)}
                                    disabled={isActioning}
                                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all ${
                                        selectedAdmin.action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 
                                        selectedAdmin.action === 'restore' ? 'bg-green-600 hover:bg-green-700' :
                                        'bg-[#174568] hover:bg-gray-800'
                                    }`}
                                >
                                    {isActioning ? <Loader2 className="animate-spin mx-auto" size={18}/> : 'Confirm'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white dark:bg-[#0b0e14] rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-[#174568] dark:text-blue-400">Invite New Admin</h3>
                            <button onClick={() => setShowInviteModal(false)} className="hover:rotate-90 transition-all text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleInvite} className="p-6 space-y-4">
                            <div className="space-y-2 text-left">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Admin Email Address</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-[#32353c] rounded-lg focus:ring-2 focus:ring-[#174568] focus:border-transparent outline-none transition-all bg-gray-50 dark:bg-inherit dark:text-white"
                                        placeholder="admin@test.com"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">An invitation email will be sent with activation instructions.</p>
                            </div>

                            <button
                                type="submit"
                                disabled={isInviting}
                                className="w-full bg-[#174568] hover:bg-gray-800 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 shadow-md hover:shadow-lg"
                            >
                                {isInviting ? <Loader2 className="animate-spin" size={18} /> : 'Send Invitation'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Dropdown Click Outside Overlay */}
            {(showHeaderDropdown || showRowDropdown) && (
                <div className="fixed inset-0 z-10" onClick={() => { setShowHeaderDropdown(false); setShowRowDropdown(null); }} />
            )}
        </>
    );
};

export default AdminsTable;
